import { InternalRefetchQueriesInclude, useMutation } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import Clear from "@mui/icons-material/Clear";
import Edit from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import { EditFacilityContactDialog } from "components/Contacts/EditFacilityContactDialog";
import { DataGrid, IssueMessageCell } from "components/DataGrid";
import {
  DisplayOnlyField,
  DisplayOnlyFieldList,
} from "components/Forms/DisplayOnlyField";
import { IssueCount } from "components/IssueCount";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import {
  ContactReportRole,
  FacilityContactsFragment,
  GetPersonQuery,
  Issue,
  Permission,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { formatPhoneNumberAndType } from "util/phoneNumbers";
import { contactRoleToLabel } from "../../../util/facilityContacts";
import { NoRowsOverlay } from "../Chemicals/Inventory/Facility/NoRowsOverlay";

const UPSERT_FACILITY_CONTACT = gql(`
  mutation UpsertFacilityContact($data: FacilityContactInput!) {
    upsertFacilityContact(data: $data) {
      id
    }
  }
`);

const DELETE_FACILITY_CONTACT = gql(`
  mutation DeleteFacilityContact($facilityContactId: ID!) {
    deleteFacilityContact(facilityContactId: $facilityContactId)
  }
`);

export type Row = NonNullable<FacilityContactsFragment["contacts"]>[number];

type FacilityContactIssueRow = (Issue & GridValidRowModel) & {
  suggestFacilityContactAdd: boolean;
};

type DataGridRow = Row | FacilityContactIssueRow;

const isFacilityContactIssueRow = (
  row: DataGridRow
): row is FacilityContactIssueRow => {
  return (row as FacilityContactIssueRow).key !== undefined;
};

export const FacilityContacts = ({
  loading,
  facilityId,
  facilityState,
  facilityContacts,
  getRowIssues,
  facilityContactIssues,
  refetchQueries,
  isReadOnly,
  excludeInternalRoles = false,
}: {
  loading: boolean;
  facilityId: string;
  facilityState: string;
  facilityContacts: FacilityContactsFragment["contacts"];
  getRowIssues: (row: Row) => Issue[];
  facilityContactIssues: Issue[];
  refetchQueries: InternalRefetchQueriesInclude;
  isReadOnly?: boolean;
  /**
   * Whether to exclude internal-only contact roles (e.g., Regional Environmental Contact)
   * from the role selection options. These roles are for internal tracking only and
   * should not appear on TierII reports. Set to true when displaying contacts in
   * the context of a TierII report.
   * @default false
   */
  excludeInternalRoles?: boolean;
}) => {
  const theme = useTheme();
  const alerts = useAlerts();
  const { tenantId = "" } = useParams();

  const [upsertFacilityContact] = useMutation(UPSERT_FACILITY_CONTACT, {
    refetchQueries,
  });

  const [deleteFacilityContact] = useMutation(DELETE_FACILITY_CONTACT, {
    refetchQueries,
  });

  const upsert = useCallback(
    async (
      person: GetPersonQuery["person"],
      roles: ContactReportRole[],
      isPrimaryContact: boolean | undefined,
      success: string
    ) => {
      if (!person) {
        return;
      }

      upsertFacilityContact({
        variables: {
          data: {
            person: {
              id: person.id.length ? person.id : undefined,
              tenantId,
              first: person.first,
              last: person.last,
              email: person.email,
              title: person.title,
              company: person.company,
              phones: person.phones?.map((p) => ({
                number: p.number,
                type: p.type,
              })),
              attentionTo: person.attentionTo,
              streetAddress1: person.streetAddress1,
              streetAddress2: person.streetAddress2,
              city: person.city,
              state: person.state,
              zip: person.zip,
              country: person.country,
              tankOwnerType: person.tankOwnerType,
              dbNumber: person.dbNumber,
            },
            facilityId,
            reportingRoles: roles,
            isPrimaryContact,
          },
        },
        onCompleted() {
          alerts.success(success);
        },
        onError() {
          alerts.error("An error occurred while saving the contact.");
        },
      });
    },
    [upsertFacilityContact, tenantId, facilityId, alerts]
  );

  const deleteContact = useCallback(
    async (facilityContactId: string) => {
      deleteFacilityContact({
        variables: { facilityContactId },
        onCompleted() {
          alerts.success("Contact removed successfully!");
        },
        onError(err) {
          alerts.error(err.message);
        },
      });
    },
    [alerts, deleteFacilityContact]
  );

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState<Row | undefined | null>(undefined);
  const [addingContact, setAddingContact] = useState<ContactReportRole>();
  const [rowToDelete, setRowToDelete] = useState<Row | undefined>(undefined);

  const openEditDialog = useCallback(
    ({ row, contact }: { row?: Row; contact?: ContactReportRole }) => {
      setIsEditDialogOpen(true);
      if (row) {
        setEditRow(row);
      } else {
        setEditRow(null);
      }
      if (contact) {
        setAddingContact(contact);
      }
    },
    [setIsEditDialogOpen, setEditRow, setAddingContact]
  );

  const closeEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
    setEditRow(undefined);
    setAddingContact(undefined);
  }, [setIsEditDialogOpen, setEditRow, setAddingContact]);

  const { hasPermissionForFacility } = useAuthorization();

  const hasWritePermission = hasPermissionForFacility(facilityId, [
    Permission.WriteFacilityContact,
  ]);

  const readOnly = isReadOnly || !hasWritePermission;

  const issueRows = facilityContactIssues.map((issue) => {
    const suggestFacilityContactAdd = Boolean(
      issue.metadata?.requiredReportingRole &&
        issue.metadata?.suggestFacilityContactAdd
    );

    return {
      ...issue,
      id: issue.message,
      metadata: issue.metadata,
      suggestFacilityContactAdd,
    };
  });

  const rows: DataGridRow[] = loading
    ? []
    : [...(facilityContacts ?? []), ...(readOnly ? [] : issueRows)];

  const columns: GridColDef<DataGridRow>[] = useMemo(
    () => [
      {
        field: "role",
        headerName: "Role",
        flex: 1,
        colSpan: (_, row) => (isFacilityContactIssueRow(row) ? 4 : undefined),
        renderCell: ({ row }) => {
          if (isFacilityContactIssueRow(row)) {
            return (
              <Box
                display="flex"
                flexDirection="column"
                height="100%"
                alignItems="start"
              >
                <IssueMessageCell issue={row} />
              </Box>
            );
          }

          if (readOnly) {
            return (
              <DisplayOnlyFieldList
                items={
                  row.reportingRoles?.map((role) => contactRoleToLabel(role)) ??
                  []
                }
                small
              />
            );
          }

          return (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {row.reportingRoles?.map((role) => (
                <Typography
                  variant="body2"
                  key={role}
                  alignItems="start"
                  noWrap
                >
                  {contactRoleToLabel(role)}
                </Typography>
              ))}
            </Box>
          );
        },
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        valueGetter: (_, row) => getFullName(row.person),
        renderCell: ({ value }) => {
          if (readOnly) {
            return <DisplayOnlyField text={value} small />;
          }
          return value;
        },
      },
      ...(facilityState === "CA"
        ? [
            {
              field: "isPrimaryContact",
              headerName: "Primary",
              flex: 0.75,
              renderCell: ({ row }: { row: DataGridRow }) => {
                if (readOnly) {
                  return (
                    <DisplayOnlyField
                      text={row.isPrimaryContact ? "Yes" : "No"}
                      small
                    />
                  );
                }
                return (
                  <Box display="flex" flexDirection="column" height="100%">
                    {row.isPrimaryContact && <CheckIcon />}
                  </Box>
                );
              },
            },
          ]
        : []),
      {
        field: "phone",
        headerName: "Phone",
        flex: 1.25,
        renderCell: ({ row }) => {
          if (isFacilityContactIssueRow(row)) return null;

          if (readOnly) {
            return (
              <DisplayOnlyFieldList
                items={
                  row.person.phones?.map((phone) =>
                    formatPhoneNumberAndType(phone)
                  ) ?? []
                }
                small
              />
            );
          }

          return (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {row.person.phones?.map((phone, index) => (
                <Typography
                  variant="body2"
                  key={index}
                  alignItems="start"
                  noWrap
                >
                  {formatPhoneNumberAndType(phone)}
                </Typography>
              ))}
            </Box>
          );
        },
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1.5,
        topAligned: true,
        valueGetter: (_, row) =>
          isFacilityContactIssueRow(row) ? "" : row.person.email ?? "",
        renderCell: ({ value }) => {
          if (readOnly) {
            return <DisplayOnlyField text={value} small />;
          }
          return (
            <Box
              sx={{
                width: "100%",
                display: "flex",
              }}
            >
              <Typography variant="body2" noWrap>
                {value}
              </Typography>
            </Box>
          );
        },
      },
      ...(readOnly
        ? [
            {
              field: "address",
              headerName: "Address",
              flex: 1.5,
              topAligned: true,
              valueGetter: (_, row) => {
                if (isFacilityContactIssueRow(row)) return "";
                const { streetAddress1, city, state, zip } = row.person;
                return [
                  streetAddress1,
                  [city, state, zip].filter(Boolean).join(", "),
                ];
              },
              renderCell: (params) => {
                if (readOnly) {
                  return <DisplayOnlyFieldList items={params.value} small />;
                }
                return (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                    }}
                  >
                    <Typography variant="body2" noWrap>
                      {params.value.join("\n")}
                    </Typography>
                  </Box>
                );
              },
            } as GridColDef<DataGridRow>,
          ]
        : []),
      ...(!readOnly
        ? [
            {
              field: "issues",
              headerName: "Issues",
              topAligned: true,
              sortable: false,
              renderCell({ row }: { row: DataGridRow }) {
                const issueCount = isFacilityContactIssueRow(row)
                  ? undefined
                  : getRowIssues(row).length;

                return <IssueCount issueCount={issueCount} />;
              },
            } as GridColDef<DataGridRow>,
          ]
        : []),
      {
        field: "actions",
        type: "actions",
        cellClassName: "actions",
        flex: 0.5,
        align: "right",
        minWidth: 130,
        renderCell({ row }) {
          if (isFacilityContactIssueRow(row)) {
            if (!row.suggestFacilityContactAdd) {
              return null;
            }

            const requiredRole = row.metadata?.requiredReportingRole as
              | ContactReportRole
              | undefined;

            if (!requiredRole) {
              return null;
            }

            return (
              <Box display="flex" height="100%" alignItems="start">
                {!readOnly && (
                  <Button
                    key="add"
                    size="small"
                    onClick={() => {
                      openEditDialog({
                        contact: requiredRole,
                      });
                    }}
                    startIcon={<Add fontSize="small" />}
                  >
                    <Box>Add Contact</Box>
                  </Button>
                )}
              </Box>
            );
          }
          return (
            <Box display="flex" height="100%" alignItems="center">
              {!readOnly && !row.person.isEncamp && (
                <Tooltip title={"Edit Contact"} key={1}>
                  <GridActionsCellItem
                    key={"edit"}
                    label={"Edit"}
                    icon={<Edit />}
                    onClick={() => {
                      openEditDialog({ row });
                    }}
                  />
                </Tooltip>
              )}
              {!readOnly && !row.person.isEncamp && (
                <Tooltip title="Delete Contact" key={2}>
                  <GridActionsCellItem
                    key="delete"
                    label="Delete"
                    icon={<Clear />}
                    onClick={() => setRowToDelete(row)}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    [getRowIssues, openEditDialog, readOnly, facilityState]
  );

  return (
    <>
      <Stack
        gap={theme.spacing(2)}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "100%",
        }}
      >
        <Stack spacing={theme.spacing(1)}>
          <Stack direction="row" justifyContent="end" alignItems="end">
            <CommandButton
              sx={{ visibility: readOnly ? "hidden" : "visible" }}
              variant="contained"
              onClick={() => {
                openEditDialog({});
              }}
            >
              Add Contact
            </CommandButton>
          </Stack>
          <DataGrid<DataGridRow>
            loading={loading}
            disableRowSelectionOnClick
            columns={columns}
            initialState={{
              sorting: { sortModel: [{ field: "name", sort: "desc" }] },
            }}
            rows={rows}
            hideFooter
            getRowClassName={(params) => {
              if (isFacilityContactIssueRow(params.row)) {
                return params.row.suggestFacilityContactAdd
                  ? "issue-row issue-row--addable"
                  : "issue-row issue-row--informational";
              }

              return "";
            }}
            slots={{
              noRowsOverlay: () => (
                <NoRowsOverlay
                  message="No contacts"
                  buttonText="Add contact"
                  showButton={!readOnly}
                  onButtonClick={() => openEditDialog({})}
                />
              ),
            }}
            onRowClick={
              !readOnly
                ? ({ row }) => {
                    if (isFacilityContactIssueRow(row)) {
                      if (!row.suggestFacilityContactAdd) {
                        return;
                      }

                      const requiredRole = row.metadata
                        ?.requiredReportingRole as
                        | ContactReportRole
                        | undefined;

                      if (!requiredRole) {
                        return;
                      }

                      openEditDialog({
                        contact: requiredRole,
                      });
                    } else {
                      openEditDialog({ row });
                    }
                  }
                : undefined
            }
            autoHeight
            getRowHeight={() => "auto"}
            sx={{
              "& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within":
                { outline: "none" },
              "& .MuiDataGrid-row:hover": {
                cursor: readOnly ? "default" : "pointer",
                backgroundColor: readOnly ? "transparent" : undefined,
              },
              "& .MuiDataGrid-row.issue-row--informational:hover": {
                cursor: "default",
                backgroundColor: readOnly ? "transparent" : undefined,
              },
              // Neutralize the hover colour (causing a flash)
              "& .MuiDataGrid-row.Mui-hovered": {
                backgroundColor: readOnly ? "transparent" : undefined,
              },

              "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                py: "4px",
                alignItems: "start",
              },
              // Hide actions on hover for non-issue rows
              "& .MuiDataGrid-row:not(.issue-row) .MuiDataGrid-cell[data-field='actions']":
                {
                  "& .MuiBox-root": {
                    opacity: 0,
                    transition: "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                },
              "& .MuiDataGrid-row:not(.issue-row):hover .MuiDataGrid-cell[data-field='actions']":
                {
                  "& .MuiBox-root": {
                    opacity: readOnly ? 0 : 1,
                  },
                },
            }}
          />
        </Stack>
      </Stack>

      {isEditDialogOpen && editRow !== undefined && (
        <EditFacilityContactDialog
          onClose={closeEditDialog}
          onSave={async (roles, person, isPrimaryContact) => {
            await upsert(
              person,
              roles,
              isPrimaryContact,
              "Contact saved successfully!"
            );
            closeEditDialog();
          }}
          data={editRow}
          contacts={facilityContacts ?? []}
          addingContact={addingContact}
          setEditRow={setEditRow}
          initialIssues={editRow ? getRowIssues(editRow) : []}
          facilityId={facilityId}
          excludeInternalRoles={excludeInternalRoles}
        />
      )}

      {rowToDelete && (
        <ConfirmDialog
          open
          onClose={() => setRowToDelete(undefined)}
          onConfirm={async () => {
            await deleteContact(rowToDelete.id);
            setRowToDelete(undefined);
          }}
          header="Are you sure you want to delete this contact?"
          resources={[
            {
              resourceType: "Contact",
              resourceName: getFullName(rowToDelete.person),
            },
          ]}
        />
      )}
    </>
  );
};
